import { z } from 'zod';

// ============================================
// AUTH SCHEMAS
// ============================================

export const registerSchema = z.object({
    email: z.string().email('Invalid email address'),
    password: z
        .string()
        .min(8, 'Password must be at least 8 characters')
        .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
        .regex(/[0-9]/, 'Password must contain at least one number'),
    name: z.string().min(2, 'Name must be at least 2 characters').max(100),
});

export const loginSchema = z.object({
    email: z.string().email('Invalid email address'),
    password: z.string().min(1, 'Password is required'),
    rememberMe: z.boolean().optional().default(false),
});

export const updatePasswordSchema = z.object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z
        .string()
        .min(8, 'Password must be at least 8 characters')
        .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
        .regex(/[0-9]/, 'Password must contain at least one number'),
});

export const forgotPasswordSchema = z.object({
    email: z.string().email('Invalid email address'),
});

export const resetPasswordSchema = z.object({
    newPassword: z
        .string()
        .min(8, 'Password must be at least 8 characters')
        .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
        .regex(/[0-9]/, 'Password must contain at least one number'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
}).refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
});

export const updateProfileSchema = z.object({
    name: z.string().min(2, 'Name must be at least 2 characters').max(100).optional(),
    avatarUrl: z.string().url().nullable().optional(),
});

// Type exports
export type RegisterInput = z.infer<typeof registerSchema>;
export type LoginInput = z.infer<typeof loginSchema>;
export type UpdatePasswordInput = z.infer<typeof updatePasswordSchema>;
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;
export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;

// ============================================
// RESPONSE TYPES
// ============================================

export interface User {
    id: string;
    email: string;
    name: string;
    avatarUrl: string | null;
    emailVerified?: boolean;
    createdAt: string;
}

export interface AuthResponse {
    user: User;
    session: {
        expiresAt: string;
    };
}

export interface Session {
    id: string;
    userAgent?: string;
    ipAddress?: string;
    createdAt: string;
    expiresAt: string;
    current: boolean;
}

// ============================================
// PROJECT SCHEMAS
// ============================================

export const createProjectSchema = z.object({
    name: z.string().min(1, 'Project name is required').max(100, 'Project name must be less than 100 characters'),
    description: z.string().max(500, 'Description must be less than 500 characters').nullable().optional(),
    color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid color format').optional().default('#6366f1'),
    iconEmoji: z.string().max(10).optional().default('📁'),
    imageUrl: z.string().url().nullable().optional(),
});

export const updateProjectSchema = z.object({
    name: z.string().min(1, 'Project name is required').max(100, 'Project name must be less than 100 characters').optional(),
    description: z.string().max(500, 'Description must be less than 500 characters').nullable().optional(),
    color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid color format').optional(),
    iconEmoji: z.string().max(10).optional(),
    imageUrl: z.string().url().nullable().optional(),
    isArchived: z.boolean().optional(),
});

export const createInvitationSchema = z.object({
    email: z.string().email('Invalid email address'),
    role: z.enum(['ADMIN', 'MEMBER', 'VIEWER']).optional().default('MEMBER'),
    expiresInDays: z.number().int().min(1).max(30).optional().default(7),
});

export type CreateProjectInput = z.infer<typeof createProjectSchema>;
export type UpdateProjectInput = z.infer<typeof updateProjectSchema>;
export type CreateInvitationInput = z.infer<typeof createInvitationSchema>;

// ============================================
// PROJECT TYPES
// ============================================

export type ProjectRole = 'OWNER' | 'ADMIN' | 'MEMBER' | 'VIEWER';

export interface Project {
    id: string;
    name: string;
    description: string | null;
    slug: string;
    iconEmoji: string | null;
    color: string | null;
    imageUrl: string | null;
    isArchived: boolean;
    ownerId: string;
    role: ProjectRole;
    membershipId?: string; // The member's own membership ID (for leaving)
    isFavorite?: boolean;
    memberCount: number;
    taskCount: number;
    createdAt: string;
    updatedAt: string;
}

export interface ProjectMember {
    id: string;
    userId: string;
    role: ProjectRole;
    joinedAt: string;
    user: {
        id: string;
        name: string;
        email: string;
        avatarUrl: string | null;
    };
}

export interface ProjectDetail extends Project {
    owner: {
        id: string;
        name: string;
        email: string;
        avatarUrl: string | null;
    };
    members: ProjectMember[];
}

export interface ProjectInvitation {
    id: string;
    email: string;
    token: string;
    role: ProjectRole;
    inviteUrl: string;
    expiresAt: string;
    createdAt: string;
}

export interface ProjectDiscordWebhookStatus {
    configured: boolean;
    enabled: boolean;
    webhookName: string | null;
    channelId: string | null;
    guildId: string | null;
    lastSuccessAt: string | null;
    lastError: string | null;
}

export interface ProjectJoinLink {
    id: string;
    role: ProjectRole;
    joinUrl: string;
    createdAt: string;
    updatedAt: string;
}

export interface JoinLinkInfo {
    role: ProjectRole;
    project: {
        id: string;
        name: string;
        iconEmoji: string | null;
        color: string | null;
        imageUrl: string | null;
    };
}

export interface InviteInfo {
    email: string;
    role: ProjectRole;
    expiresAt: string;
    project: {
        id: string;
        name: string;
        iconEmoji: string | null;
        color: string | null;
        imageUrl: string | null;
    };
}

// ============================================
// TASK SCHEMAS
// ============================================

export const taskStatusSchema = z.enum(['TODO', 'IN_PROGRESS', 'IN_REVIEW', 'DONE', 'CANCELLED']);
export const taskPrioritySchema = z.enum(['NONE', 'LOW', 'MEDIUM', 'HIGH', 'URGENT']);

export const createTaskSchema = z.object({
    title: z.string().min(1, 'Task title is required').max(500, 'Task title must be less than 500 characters'),
    description: z.string().max(10000, 'Description must be less than 10000 characters').nullable().optional(),
    status: taskStatusSchema.optional().default('TODO'),
    priority: taskPrioritySchema.optional().default('NONE'),
    dueDate: z.string().datetime().nullable().optional(),
    parentId: z.string().nullable().optional(),
    assigneeIds: z.array(z.string()).optional().default([]),
    customFieldValues: z.record(z.any()).optional().default({}),
});

export const updateTaskSchema = z.object({
    title: z.string().min(1, 'Task title is required').max(500, 'Task title must be less than 500 characters').optional(),
    description: z.string().max(10000, 'Description must be less than 10000 characters').nullable().optional(),
    status: taskStatusSchema.optional(),
    priority: taskPrioritySchema.optional(),
    dueDate: z.string().datetime().nullable().optional(),
    // R2: panel-level date pickers for startDate / endDate — the
    // backend already accepts these in updateTaskSchema (since t17)
    // but the frontend's mirror schema was missing them, which made
    // the panel's PATCH calls fail to type-check.
    startDate: z.string().datetime().nullable().optional(),
    endDate: z.string().datetime().nullable().optional(),
    completedAt: z.string().datetime().nullable().optional(),
    parentId: z.string().nullable().optional(),
    customFieldValues: z.record(z.any()).optional(),
});

export type TaskStatus = z.infer<typeof taskStatusSchema>;
export type TaskPriority = z.infer<typeof taskPrioritySchema>;
export type CreateTaskInput = z.infer<typeof createTaskSchema>;
export type UpdateTaskInput = z.infer<typeof updateTaskSchema>;

// ============================================
// TASK TYPES
// ============================================

export interface TaskAssignee {
    id: string;
    userId: string;
    assignedAt: string;
    user: {
        id: string;
        name: string;
        email: string;
        avatarUrl: string | null;
    };
}

export interface Task {
    id: string;
    title: string;
    description: string | null;
    order: number;
    status: TaskStatus;
    priority: TaskPriority;
    dueDate: string | null;
    // t14: startDate/endDate are auto-managed by the backend status
    // hooks service (task-status-hooks.service.ts). They surface in
    // the Gantt bars and the task-detail panel as read-only labels.
    startDate: string | null;
    endDate: string | null;
    completedAt: string | null;
    customFieldValues: Record<string, any>;
    projectId: string;
    parentId: string | null;
    assignees: TaskAssignee[];
    subtaskCount: number;
    commentCount: number;
    attachmentCount: number;
    createdAt: string;
    updatedAt: string;
}

export interface TaskDetail extends Task {
    project: {
        id: string;
        name: string;
        color: string | null;
    };
    subtasks: Task[];
    comments?: Comment[];
    attachments?: Attachment[];
}

// ============================================
// COMMENT SCHEMAS & TYPES
// ============================================

export const createCommentSchema = z.object({
    content: z.string().min(1, 'Comment content is required').max(10000, 'Comment must be less than 10000 characters'),
    mentions: z.array(z.string()).optional().default([]),
});

export const updateCommentSchema = z.object({
    content: z.string().min(1, 'Comment content is required').max(10000, 'Comment must be less than 10000 characters'),
    mentions: z.array(z.string()).optional().default([]),
});

export type CreateCommentInput = z.infer<typeof createCommentSchema>;
export type UpdateCommentInput = z.infer<typeof updateCommentSchema>;

export interface Comment {
    id: string;
    content: string;
    taskId: string;
    authorId: string;
    author: {
        id: string;
        name: string;
        email: string;
        avatarUrl: string | null;
    };
    mentions?: string[];
    createdAt: string;
    updatedAt: string;
}

// ============================================
// ATTACHMENT SCHEMAS & TYPES
// ============================================

export const createAttachmentSchema = z.object({
    filename: z.string().min(1, 'Filename is required'),
    originalName: z.string().min(1, 'Original filename is required'),
    mimeType: z.string().min(1, 'MIME type is required'),
    size: z.number().positive('File size must be positive'),
    url: z.string().url().optional(),
});

export const presignedUrlSchema = z.object({
    filename: z.string().min(1, 'Filename is required'),
    mimeType: z.string().min(1, 'MIME type is required'),
    size: z.number().positive('File size must be positive'),
});

export type CreateAttachmentInput = z.infer<typeof createAttachmentSchema>;
export type PresignedUrlInput = z.infer<typeof presignedUrlSchema>;

export interface Attachment {
    id: string;
    filename: string;
    originalName: string;
    mimeType: string;
    size: number;
    url: string;
    taskId: string;
    uploadedById: string;
    uploadedBy: {
        id: string;
        name: string;
        email: string;
        avatarUrl: string | null;
    };
    createdAt: string;
}

export interface PresignedUrlResponse {
    uploadUrl: string;
    fileKey: string;
    expiresIn: number;
}

// ============================================
// SAVED VIEW SCHEMAS & TYPES
// ============================================

// The default set of columns shown on a fresh status-grouped Kanban board.
// Exported so both the auto-seeded server response and the first-render UI
// agree on the column order without duplicating the literal.
export const DEFAULT_KANBAN_COLUMNS = ['TODO', 'IN_PROGRESS', 'IN_REVIEW', 'DONE', 'CANCELLED'] as const;

// Default field that drives a Kanban board's columns. The backend may also
// point this at a SELECT custom field id for advanced users.
export const DEFAULT_KANBAN_GROUPING = 'status' as const;

// Calendar: the field whose date drives where a task lands. Mirrors the
// backend `buildDefaultCalendarViewConfig()` shape so a freshly-seeded
// view matches the default the first time ?type=CALENDAR is fetched.
export const CALENDAR_DATE_SOURCES = ['dueDate', 'startDate', 'endDate', 'sprintStart', 'sprintEnd'] as const;
export const DEFAULT_CALENDAR_DATE_SOURCE = 'dueDate' as const;

export const CALENDAR_VIEW_MODES = ['month', 'week', 'agenda'] as const;
export const DEFAULT_CALENDAR_VIEW_MODE = 'month' as const;

// Color rule for a calendar pill. 'status' | 'priority' | 'assignee' | <customFieldId>.
// Free-form string so advanced users can point at a SELECT custom field.
export const DEFAULT_CALENDAR_COLOR_BY = 'status' as const;

// R14: DOCUMENT is the new Google-Docs-like rich text mode. Mirrors
// the backend enum in backend/src/schemas/view.ts.
export const viewTypeSchema = z.enum(['TABLE', 'KANBAN', 'CALENDAR', 'GANTT', 'DOCUMENT', 'CHAT']);

// t43 (R13): per-type custom-field filter shape. Mirrors
// backend/src/schemas/view.ts. Operators are validated at
// runtime per `type` — the schema accepts any string for `op`
// to keep the transport layer permissive.
const customFieldFilterSchema = z.object({
    type: z.enum(['TEXT', 'NUMBER', 'DATE', 'SELECT', 'MULTI_SELECT', 'CHECKBOX', 'URL', 'EMAIL', 'PERSON', 'SPRINT']),
    op: z.string().min(1),
    value: z.unknown().optional(),
    value2: z.unknown().optional(),
});

const customFieldsFilterValueSchema = z.union([
    customFieldFilterSchema,
    z.array(z.string()),
]);

export const filterConfigSchema = z.object({
    status: z.array(z.string()).optional(),
    priority: z.array(z.string()).optional(),
    assignees: z.array(z.string()).optional(),
    dueDateFrom: z.string().optional(),
    dueDateTo: z.string().optional(),
    search: z.string().optional(),
    customFields: z.record(customFieldsFilterValueSchema).optional(),
}).passthrough();

export const sortConfigSchema = z.object({
    field: z.string(),
    order: z.enum(['asc', 'desc']),
});

// One condition on a single task field. Used inside ColumnDef.filterRules.
export const filterRuleSchema = z.object({
    field: z.string().min(1),
    operator: z.enum(['eq', 'neq', 'in', 'notIn', 'isEmpty', 'isNotEmpty', 'contains']),
    value: z.union([z.string(), z.array(z.string())]).optional(),
});
export type FilterRule = z.infer<typeof filterRuleSchema>;

// One lane of a Kanban board. Tasks whose every filterRule matches live
// in this column. Hidden columns are still persisted (so unhide works)
// but their tasks fall through to the synthesized "Other" column.
export const columnDefSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1).max(80),
    color: z.string().optional(),
    filterRules: z.array(filterRuleSchema).min(1),
    visible: z.boolean().optional().default(true),
});
export type ColumnDef = z.infer<typeof columnDefSchema>;

export const viewConfigSchema = z.object({
    columns: z.array(z.string()).optional(),
    columnOrder: z.array(z.string()).optional(),
    columnWidths: z.record(z.number()).optional(),
    hiddenColumns: z.array(z.string()).optional(), // columns hidden by user
    filters: filterConfigSchema.optional(),
    sorts: z.array(sortConfigSchema).optional(),
    groupBy: z.string().nullable().optional(),

    // Kanban-specific: the field whose values become board columns. Default
    // 'status'; advanced users can point at a SELECT custom field id.
    columnGrouping: z.string().optional(),

    // Kanban-specific: the ordered list of columns shown on the board.
    // Each entry is a ColumnDef (id, name, color?, filterRules, visible?).
    // The backend normalizes legacy string[] entries to ColumnDef[] on
    // read so the frontend always sees one shape.
    kanbanColumns: z.array(columnDefSchema).optional(),

    // Calendar-specific (t16). All three fields are optional so a frontend
    // can persist a partial config while iterating on the UI; the seed
    // from buildDefaultCalendarViewConfig() always includes all three.
    viewMode: z.enum(['month', 'week', 'agenda']).optional(),
    // T21 R4: 'all' is the display-only sentinel meaning "show every
    // task regardless of its date columns" — handled client-side in
    // resolveTaskDate (the backend doesn't filter on this value).
    dateSource: z
        .enum(['dueDate', 'startDate', 'endDate', 'sprintStart', 'sprintEnd', 'all'])
        .optional(),
    colorBy: z.string().optional(),
    // T21 R4: user-defined date ranges rendered as bands. Mirrors the
    // backend view schema (view.ts) — kept permissive via .passthrough()
    // at the root so future tweaks don't break round-trips.
    periods: z
        .array(
            z.object({
                id: z.string(),
                name: z.string(),
                startDate: z.string(),
                endDate: z.string(),
                color: z.string().optional(),
            }).passthrough(),
        )
        .optional(),

    // Gantt-specific (t14).
    timeScale: z.enum(['day', 'week', 'month', 'auto']).optional(),
    showSprints: z.boolean().optional(),
    ganttGroupBy: z.enum(['status', 'assignee', 'none']).optional(),
}).passthrough();

export const createSavedViewSchema = z.object({
    name: z.string().min(1, 'View name is required').max(100),
    isDefault: z.boolean().optional().default(false),
    isShared: z.boolean().optional().default(false),
    addToFavorites: z.boolean().optional().default(false),
    type: viewTypeSchema.optional().default('TABLE'),
    config: viewConfigSchema.optional().default({}),
    // Kanban-only: which custom field ids appear as chips on each card.
    // Ignored for TABLE views.
    visibleCardFields: z.array(z.string()).optional(),
});

export const updateSavedViewSchema = z.object({
    name: z.string().min(1, 'View name is required').max(100).optional(),
    isDefault: z.boolean().optional(),
    isShared: z.boolean().optional(),
    type: viewTypeSchema.optional(),
    config: viewConfigSchema.optional(),
    visibleCardFields: z.array(z.string()).optional().nullable(),
});

export type ViewType = z.infer<typeof viewTypeSchema>;
export type FilterConfig = z.infer<typeof filterConfigSchema>;
export type SortConfig = z.infer<typeof sortConfigSchema>;
export type ViewConfig = z.infer<typeof viewConfigSchema>;
export type CreateSavedViewInput = z.infer<typeof createSavedViewSchema>;
export type UpdateSavedViewInput = z.infer<typeof updateSavedViewSchema>;

export interface SavedView {
    id: string;
    name: string;
    isDefault: boolean;
    isShared: boolean;
    isFavorite: boolean;
    favoritePosition: number | null;
    position: number;
    type: ViewType;
    config: ViewConfig;
    visibleCardFields: string[] | null;
    projectId: string;
    userId: string;
    createdAt: string;
    updatedAt: string;
    // R14: DOCUMENT-only. Mirrors the backend response shape — the raw
    // snapshot is too large to embed in list responses so the
    // frontend fetches it on demand via
    // GET /api/views/:viewId/document-snapshot.
    hasDocumentSnapshot?: boolean;
    documentSnapshotBytes?: number;
}

// ============================================
// COLUMN CRUD SCHEMAS
// ============================================

export const createColumnBodySchema = z.object({
    name: z.string().min(1).max(80),
    color: z.string().optional(),
    filterRules: z.array(filterRuleSchema).min(1),
});

export const updateColumnBodySchema = z.object({
    name: z.string().min(1).max(80).optional(),
    color: z.string().optional(),
    filterRules: z.array(filterRuleSchema).min(1).optional(),
    visible: z.boolean().optional(),
});

export const reorderColumnsBodySchema = z.object({
    columnIds: z.array(z.string().min(1)).min(1),
});

export type CreateColumnBody = z.infer<typeof createColumnBodySchema>;
export type UpdateColumnBody = z.infer<typeof updateColumnBodySchema>;
export type ReorderColumnsBody = z.infer<typeof reorderColumnsBodySchema>;

// ============================================
// NOTIFICATION TYPES
// ============================================

export type NotificationType =
    | 'TASK_ASSIGNED'
    | 'TASK_UPDATED'
    | 'COMMENT_ADDED'
    | 'MENTION'
    | 'PROJECT_INVITE'
    | 'DUE_DATE_REMINDER';

export interface NotificationData {
    taskId?: string;
    taskTitle?: string;
    projectId?: string;
    projectName?: string;
    commentId?: string;
    userId?: string;
    userName?: string;
    inviteToken?: string;
    [key: string]: string | undefined;
}

export interface Notification {
    id: string;
    type: NotificationType;
    title: string;
    message: string;
    isRead: boolean;
    data: NotificationData | null;
    createdAt: string;
}

export interface NotificationsResponse {
    notifications: Notification[];
    unreadCount: number;
    nextCursor?: string;
}

// ============================================
// CUSTOM FIELD TYPES
// ============================================

export type CustomFieldType =
    | 'TEXT'
    | 'NUMBER'
    | 'DATE'
    | 'SELECT'
    | 'MULTI_SELECT'
    | 'CHECKBOX'
    | 'URL'
    | 'EMAIL'
    | 'PERSON'
    // t16: SPRINT stores a list of sprints in CustomField.options
    // (JSON shape: { sprints: [{id, name, startDate, endDate, color}, ...] })
    // and the selected sprint id in task.customFieldValues[customFieldId].
    // The backend added it to the Prisma enum in t14; the frontend now
    // mirrors it so the Calendar view can render sprint bands + colour
    // pills by sprint.
    | 'SPRINT';

export interface CustomFieldOption {
    id: string;
    label: string;
    color?: string;
}

export interface CustomField {
    id: string;
    name: string;
    type: CustomFieldType;
    order: number;
    isRequired: boolean;
    options: CustomFieldOption[];
    projectId: string;
    createdAt: string;
    updatedAt: string;
}

export interface CreateCustomFieldInput {
    name: string;
    type: CustomFieldType;
    isRequired?: boolean;
    options?: CustomFieldOption[];
}

export interface UpdateCustomFieldInput {
    name?: string;
    isRequired?: boolean;
    options?: CustomFieldOption[];
    order?: number;
}

// ============================================
// PROJECT TASK IMPORT / EXPORT
// ============================================

export interface ProjectTransferTask {
    id?: string;
    title: string;
    description?: string | null;
    order: number;
    status: TaskStatus;
    priority: TaskPriority;
    dueDate?: string | null;
    completedAt?: string | null;
    assignees: string[];
    customFieldValues: Record<string, unknown>;
    createdAt?: string;
    updatedAt?: string;
    subtasks: ProjectTransferTask[];
    // R25 (t67): per-entry deletion marker. `{ "id": "...", "deleted":
    // true }` deletes the task (and its subtasks) on import instead of
    // creating/updating it; `title` is then optional.
    deleted?: boolean;
}

export interface ProjectTransferDocument {
    format: 'project-manager-task-export';
    version: 1;
    /** Documentation block, serialized first. Ignored by the import. */
    _readme?: string;
    exportedAt?: string;
    project?: { id?: string; name?: string };
    members: Array<{ id: string; email: string; name?: string }>;
    customFields: Array<{
        id: string;
        name: string;
        type: CustomFieldType;
        order: number;
        isRequired: boolean;
        options: CustomFieldOption[];
    }>;
    // R25 (t67): ids of tasks to delete on import.
    deletedTasks?: string[];
    tasks: ProjectTransferTask[];
}

export interface ProjectImportSummary {
    tasksCreated: number;
    tasksUpdated: number;
    /** R25 (t67): tasks removed by the import (explicit deletions only). */
    tasksDeleted: number;
    customFieldsCreated: number;
    customFieldsUpdated: number;
    optionsAdded: number;
    warnings: string[];
}
